import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';

const dbRowKey = (row) => `${row.database_key || ''}|${row.database_name || ''}`;

const formatMoney = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatInt = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : String(Math.round(n));
};

const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const asArray = (v) => (Array.isArray(v) ? v : []);

const modeRowTx = (row) => {
  const r = asObject(row);
  return r.total_transactions ?? r.transaction_count;
};

const entityRowTx = (row) => {
  const r = asObject(row);
  return r.total_transactions ?? r.transaction_count;
};

function PaymentMonthDetailContent({ detail }) {
  const summary = asObject(detail.summary);
  const entities = asArray(detail.by_entity);
  const modes = asArray(detail.by_mode_of_payment);

  const metaRows = [
    { label: 'Database', value: `${detail.database_name || '—'} · key: ${detail.database_key || '—'}` },
    { label: 'Report tab', value: detail.tab_name || '—' },
    { label: 'Period', value: detail.year_month_label || detail.year_month || '—' },
    { label: 'Year-month', value: detail.year_month || '—' },
    { label: 'Institute', value: detail.institute_name || '—' },
    { label: 'Coordinator', value: detail.coordinator_name || '—' },
    { label: 'Sync run', value: detail.transaction_id || '—' },
  ];

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Grid container spacing={1} columnSpacing={2}>
          {metaRows.map(({ label, value }) => (
            <React.Fragment key={label}>
              <Grid item xs={12} sm={4} md={3}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {label}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={9}>
                <Typography variant="body2">{value}</Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Paper>

      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          Month totals
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ height: '100%', borderLeft: 4, borderColor: 'primary.main' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total amount collected
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                  {formatMoney(summary.total_amount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ height: '100%', borderLeft: 4, borderColor: 'success.main' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Number of transactions
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                  {formatInt(summary.total_transactions ?? summary.transaction_count)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" sx={{ height: '100%', borderLeft: 4, borderColor: 'warning.main' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total transaction fees
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                  {formatMoney(summary.total_transaction_fees)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          By entity 
        </Typography>
        {entities.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No per-entity breakdown for this month.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Entity</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Transactions</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Fees</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entities.map((raw, idx) => {
                  const row = asObject(raw);
                  const label = row.entity_label || row.entity_name || '—';
                  const code = row.entity_name && row.entity_label && row.entity_name !== row.entity_label
                    ? row.entity_name
                    : (row.entity_code || row.code || '');
                  return (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{label}</Typography>
                        {code ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Code: {code}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{formatInt(entityRowTx(row))}</TableCell>
                      <TableCell align="right">{formatMoney(row.total_amount)}</TableCell>
                      <TableCell align="right">{formatMoney(row.total_transaction_fees)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          By mode of payment
        </Typography>
        {modes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No payment-mode breakdown for this month.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Transactions</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Fees</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modes.map((raw, idx) => {
                  const row = asObject(raw);
                  const code = row.mode_of_payment || row.mode || '—';
                  const human = row.mode_label || row.label || row.mode_name || '';
                  return (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{human || code}</Typography>
                        {human && code && human !== code ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Code: {code}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{formatInt(modeRowTx(row))}</TableCell>
                      <TableCell align="right">{formatMoney(row.total_amount)}</TableCell>
                      <TableCell align="right">{formatMoney(row.total_transaction_fees)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Stack>
  );
}

function ClientPaymentGatewayList(props) {
  const [databases, setDatabases] = React.useState([]);
  const [tabs, setTabs] = React.useState([]);
  const [coordinatorOptions, setCoordinatorOptions] = React.useState([]);
  const [tabFilter, setTabFilter] = React.useState('');
  const [databaseFilter, setDatabaseFilter] = React.useState('');
  const [yearMonthFilter, setYearMonthFilter] = React.useState('');
  const [coordinatorFilter, setCoordinatorFilter] = React.useState('');
  const [dbLoading, setDbLoading] = React.useState(false);
  const [dbPage, setDbPage] = React.useState(0);
  const [dbPageSize, setDbPageSize] = React.useState(10);
  const [dbRowCount, setDbRowCount] = React.useState(0);
  const [lastSyncTime, setLastSyncTime] = React.useState(null);

  const [expandedKey, setExpandedKey] = React.useState(null);
  const [monthsRows, setMonthsRows] = React.useState([]);
  const [monthsLoading, setMonthsLoading] = React.useState(false);
  const [expandedDb, setExpandedDb] = React.useState(null);

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [monthDetail, setMonthDetail] = React.useState(null);

  const filterParams = React.useMemo(() => ({
    ...(tabFilter && { tab_name: tabFilter }),
    ...(databaseFilter.trim() && { database_name: databaseFilter.trim() }),
    ...(yearMonthFilter.trim() && { year_month: yearMonthFilter.trim() }),
    ...(coordinatorFilter && { coordinator: coordinatorFilter }),
  }), [coordinatorFilter, databaseFilter, tabFilter, yearMonthFilter]);

  const fetchCoordinatorOptions = React.useCallback(() => {
    getRequest(
      GET_URL.client_payment_gateway_report.api,
      { coordinator_options: 1 },
      props
    ).then((response) => {
      if (response?.status === 200) {
        setCoordinatorOptions(response.data?.data || []);
      }
    }).catch(() => {});
  }, [props]);

  React.useEffect(() => {
    fetchCoordinatorOptions();
  }, [fetchCoordinatorOptions]);

  const fetchDatabases = React.useCallback(() => {
    setDbLoading(true);
    getRequest(
      GET_URL.client_payment_gateway_report.api,
      {
        list_databases: 1,
        limit: dbPageSize,
        pageno: dbPage + 1,
        ...filterParams,
      },
      props
    ).then((response) => {
      setDbLoading(false);
      if (response?.status === 200) {
        setDatabases(response.data?.data || []);
        setTabs(response.data?.tabs || []);
        setDbRowCount(response.data?.count || 0);
        setLastSyncTime(response.data?.last_sync_time || null);
      }
    }).catch(() => setDbLoading(false));
  }, [dbPage, dbPageSize, filterParams, props]);

  React.useEffect(() => {
    fetchDatabases();
  }, [fetchDatabases]);

  React.useEffect(() => {
    setDbPage(0);
  }, [tabFilter, databaseFilter, yearMonthFilter, coordinatorFilter]);

  React.useEffect(() => {
    setExpandedKey(null);
    setExpandedDb(null);
    setMonthsRows([]);
  }, [dbPage, dbPageSize]);

  const fetchMonthsForDatabase = React.useCallback((dbKey, dbName) => {
    setMonthsLoading(true);
    setMonthsRows([]);
    getRequest(
      GET_URL.client_payment_gateway_report.api,
      {
        months_for_database: 1,
        database_key: dbKey,
        database_name: dbName,
        limit: 200,
        pageno: 1,
        ...filterParams,
      },
      props
    ).then((response) => {
      setMonthsLoading(false);
      if (response?.status === 200) {
        setMonthsRows(response.data?.data || []);
      }
    }).catch(() => {
      setMonthsLoading(false);
      setMonthsRows([]);
    });
  }, [filterParams, props]);

  React.useEffect(() => {
    if (!expandedKey || !expandedDb) {
      setMonthsRows([]);
      return;
    }
    fetchMonthsForDatabase(expandedDb.database_key, expandedDb.database_name);
  }, [expandedDb, expandedKey, fetchMonthsForDatabase]);

  const toggleExpand = (row) => {
    const key = dbRowKey(row);
    if (expandedKey === key) {
      setExpandedKey(null);
      setExpandedDb(null);
      setMonthsRows([]);
      return;
    }
    setExpandedKey(key);
    setExpandedDb({ database_key: row.database_key, database_name: row.database_name });
  };

  const openMonthDetail = (monthId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setMonthDetail(null);
    const base = GET_URL.client_payment_gateway_report.api;
    const url = `${base}${monthId}/`;
    getRequest(url, {}, props).then((response) => {
      setDetailLoading(false);
      if (response?.status === 200) {
        setMonthDetail(response.data || null);
      }
    }).catch(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setMonthDetail(null);
  };

  const parentColSpan = 9;

  return (
    <Box p={2}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" fontWeight={700}>Payment Gateway</Typography>
          <Typography variant="body2" color="text.secondary">
            Expand a database to load its months (API). Click a month to load full JSON for that row (API).
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last data update: {lastSyncTime ? new Date(lastSyncTime).toLocaleString() : 'No rows stored yet'}
          </Typography>
        </Grid>
      </Grid>

      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Report date (tab)"
              value={tabFilter}
              onChange={(e) => { setTabFilter(e.target.value); setDbPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {tabs.map((tab) => (
                <MenuItem key={tab} value={tab}>{tab}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Database name contains"
              value={databaseFilter}
              onChange={(e) => { setDatabaseFilter(e.target.value); setDbPage(0); }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Year-month (YYYY-MM)"
              placeholder="2025-05"
              value={yearMonthFilter}
              onChange={(e) => { setYearMonthFilter(e.target.value); setDbPage(0); }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Institute POC (from client report)"
              value={coordinatorFilter}
              onChange={(e) => { setCoordinatorFilter(e.target.value); setDbPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              {coordinatorOptions.map((opt) => (
                <MenuItem key={opt.id} value={String(opt.id)}>{opt.name || opt.id}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {dbLoading && <LinearProgress sx={{ mb: 1 }} />}

      <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell width={44} />
                <TableCell sx={{ fontWeight: 700 }}>Database</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>DB key</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Institute</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Coordinator</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Months</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Txns</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Fees</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!dbLoading && databases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={parentColSpan}>
                    <Typography align="center" sx={{ py: 4 }} color="text.secondary">
                      No databases found. Sync client report with payment data.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : databases.map((row) => {
                const key = dbRowKey(row);
                const open = expandedKey === key;
                return (
                  <React.Fragment key={key}>
                    <TableRow hover selected={open}>
                      <TableCell padding="checkbox">
                        <IconButton size="small" onClick={() => toggleExpand(row)} aria-label={open ? 'Collapse' : 'Expand'}>
                          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{row.database_name || '—'}</Typography>
                        {(row.tab_names && row.tab_names.length > 0) && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Tab: {row.tab_names.join(', ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.database_key || '—'}</TableCell>
                      <TableCell>{row.institute_name || '—'}</TableCell>
                      <TableCell>{row.coordinator_name || '—'}</TableCell>
                      <TableCell align="right">{formatInt(row.month_count)}</TableCell>
                      <TableCell align="right">{formatInt(row.transaction_count)}</TableCell>
                      <TableCell align="right">{formatMoney(row.total_amount)}</TableCell>
                      <TableCell align="right">{formatMoney(row.total_transaction_fees)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0, borderBottom: open ? undefined : 0 }} colSpan={parentColSpan}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2, backgroundColor: '#fbfdff' }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                              Months for this database
                            </Typography>
                            {monthsLoading ? (
                              <LinearProgress />
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>YM</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Summary tx</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Summary amt</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Summary fees</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {monthsRows.length ? monthsRows.map((m) => {
                                    const s = m.summary && typeof m.summary === 'object' ? m.summary : {};
                                    return (
                                      <TableRow hover key={m.id}>
                                        <TableCell>{m.year_month_label || '—'}</TableCell>
                                        <TableCell>{m.year_month || '—'}</TableCell>
                                        <TableCell align="right">{formatInt(s.total_transactions ?? s.transaction_count)}</TableCell>
                                        <TableCell align="right">{formatMoney(s.total_amount)}</TableCell>
                                        <TableCell align="right">{formatMoney(s.total_transaction_fees)}</TableCell>
                                        <TableCell>
                                          <Button size="small" variant="outlined" onClick={() => openMonthDetail(m.id)}>
                                            View full row
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  }) : (
                                    <TableRow>
                                      <TableCell colSpan={6}>
                                        <Typography variant="body2" color="text.secondary">No month rows.</Typography>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={dbRowCount}
          page={dbPage}
          rowsPerPage={dbPageSize}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Databases per page"
          onPageChange={(e, p) => setDbPage(p)}
          onRowsPerPageChange={(e) => {
            setDbPageSize(parseInt(e.target.value, 10));
            setDbPage(0);
          }}
        />
      </Paper>

      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle>
          Payment gateway {monthDetail ? `· ${monthDetail.year_month_label}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? <LinearProgress /> : null}
          {!detailLoading && monthDetail ? (
            <PaymentMonthDetailContent detail={monthDetail} />
          ) : !detailLoading ? (
            <Typography color="text.secondary">Could not load detail.</Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ClientPaymentGatewayList;
